import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { gsap } from 'gsap'
import { Helmet } from 'react-helmet-async'
import Header from '../components/Header'
import Footer from '../components/Footer'
import PageMeta from '../components/PageMeta'
import CommentSection from '../components/CommentSection'

const BlogPostPage = () => {
    const { slug } = useParams()
    const [post, setPost] = useState(null)
    const [loading, setLoading] = useState(true)
    const [relatedPosts, setRelatedPosts] = useState([])
    const [userRating, setUserRating] = useState(0)
    const [hoveredRating, setHoveredRating] = useState(0)
    const [hasRated, setHasRated] = useState(false)

    useEffect(() => {
        fetchPost()
    }, [slug])

    useEffect(() => {
        if (post && !loading) {
            gsap.to('.blog-post-title', {
                opacity: 1,
                duration: 1.2,
                ease: 'power2.out',
                delay: 0.2
            })
        }
    }, [post, loading])

    async function fetchPost() {
        try {
            const apiUrl = `/api/blog/posts/${slug}`
            
            console.log('Fetching from:', apiUrl)
            const response = await fetch(apiUrl)
            const data = await response.json()
            console.log('Received data:', data)
            console.log('Post object:', data.post)
            setPost(data.post)

            // Fetch related posts
            if (data.post) {
                fetchRelatedPosts(data.post.category)
            }
        } catch (error) {
            console.error('Error loading post:', error)
        } finally {
            setLoading(false)
            // Force show title after load
            setTimeout(() => {
                const titleEl = document.querySelector('.blog-post-header .title')
                if (titleEl) {
                    titleEl.style.opacity = '1'
                    titleEl.style.visibility = 'visible'
                }
            }, 100)
        }
    }

    async function fetchRelatedPosts(postId) {
        try {
            const apiUrl = `/api/blog/posts/${slug}/related`
            
            const response = await fetch(apiUrl)
            const data = await response.json()
            if (data.success) {
                setRelatedPosts(data.posts || [])
            }
        } catch (error) {
            console.error('Error loading related posts:', error)
        }
    }

    // Check if user has already rated
    useEffect(() => {
        const hasRatedBefore = localStorage.getItem(`rated-${slug}`)
        if (hasRatedBefore) {
            setHasRated(true)
        }
    }, [slug])

    // Handle rating submission
    const handleRating = async (rating) => {
        if (hasRated) return

        try {
            const apiUrl = `/api/blog/posts/${slug}/rate`
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating })
            })

            if (response.ok) {
                const data = await response.json()
                setPost(prev => ({
                    ...prev,
                    ratings: data.ratings
                }))
                setUserRating(rating)
                setHasRated(true)
                localStorage.setItem(`rated-${slug}`, 'true')
            }
        } catch (error) {
            console.error('Rating error:', error)
        }
    }

    if (loading) {
        return (
            <div>
                <Header />
                <main className="main-content">
                    <section className="section">
                        <div className="section-inner" style={{ textAlign: 'center', padding: '4rem 0' }}>
                            <h1 className="title title-h2">Loading...</h1>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        )
    }
    console.log('Post state after loading:', post)
    console.log('Post exists?', !!post)
    console.log('Post title?', post?.title)
    if (!post) {
        return (
            <div>
                <Header />
                <main className="main-content">
                    <section className="section">
                        <div className="section-inner" style={{ textAlign: 'center', padding: '4rem 0' }}>
                            <h1 className="title title-h2">Post Not Found</h1>
                            <p className="description">Sorry, the blog post you're looking for doesn't exist.</p>
                            <Link to="/blog" className="btn btn-primary" style={{ marginTop: '2rem' }}>
                                Back to Blog
                            </Link>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        )
    }

    return (
        <div>
            <PageMeta
                title={`${post.title} | RDS Studio Blog`}
                description={post.excerpt}
                canonical={`https://rdsstudio.co.za/blog/${post.slug}`}
                ogImage={post.featuredImage
                    ? (post.featuredImage.startsWith('http')
                        ? post.featuredImage
                        : 'https://rdsstudio.co.za' + post.featuredImage)
                    : 'https://rdsstudio.co.za/images/logo/og-image.png'
                }
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": post.title,
                        "description": post.excerpt || post.metaDescription,
                        "image": post.featuredImage || "https://rdsstudio.co.za/images/logo/og-image.png",
                        "datePublished": post.publishDate || post.createdAt,
                        "dateModified": post.updatedAt || post.publishDate || post.createdAt,
                        "author": {
                            "@type": "Person",
                            "@id": "https://rdsstudio.co.za/#founder",
                            "name": post.author || "JP - RDS Studio",
                            "jobTitle": "Web Designer & Digital Marketing Specialist",
                            "url": "https://rdsstudio.co.za/about",
                            "description": "Professional web developer and digital marketing specialist with expertise in web development, digital marketing, and intelligent automation"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "@id": "https://rdsstudio.co.za/#organization",
                            "name": "RDS Studio",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://rdsstudio.co.za/images/logo/RDS%20Logo.png"
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `https://rdsstudio.co.za/blog/${post.slug}`
                        },
                        "articleSection": post.category,
                        "keywords": post.tags ? post.tags.join(', ') : post.category,
                        "wordCount": post.content ? post.content.split(' ').length : 0,
                        "articleBody": post.content,
                        "inLanguage": "en-ZA",
                        "about": post.category,
                        "aggregateRating": post.ratings?.count > 0 ? {
                            "@type": "AggregateRating",
                            "ratingValue": post.ratings.average,
                            "ratingCount": post.ratings.count,
                            "bestRating": "5",
                            "worstRating": "1"
                        } : undefined
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://rdsstudio.co.za"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Blog",
                                "item": "https://rdsstudio.co.za/blog"
                            },
                            {
                                "@type": "ListItem",
                                "position": 3,
                                "name": post.title,
                                "item": `https://rdsstudio.co.za/blog/${post.slug}`
                            }
                        ]
                    })}
                </script>
            </Helmet>
            <Header />
            <main className="main-content">
                <article className="blog-post-page">
                    {console.log('Rendering post:', post)}
                    {console.log('Post title:', post?.title)}
                    <div className="section-inner">
                        {/* Breadcrumb */}
                        <nav className="breadcrumb">
                            <Link to="/">Home</Link>
                            <span>/</span>
                            <Link to="/blog">Blog</Link>
                            <span>/</span>
                            <span>{post.title}</span>
                        </nav>

                        {/* Post Header */}
                        <header className="blog-post-header">
                            <span className="blog-post-category">{post.category}</span>
                            <h1 className="title title-h2 blog-post-title">{post.title}</h1>
                            <div className="blog-post-meta">
                                <span className="blog-post-author">By {post.author}</span>
                                <span> | </span>
                                <span className="blog-post-date">{new Date(post.publishDate || post.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                <span> | </span>
                                <span className="blog-post-readtime">{post.readTime} min read</span>
                            </div>

                            {/* Star Rating */}
                            <div className="blog-post-rating">
                                <div className="rating-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <span
                                            key={star}
                                            className={`star ${star <= (hoveredRating || userRating || post.ratings?.average || 0) ? 'filled' : ''} ${hasRated ? 'disabled' : ''}`}
                                            onClick={() => handleRating(star)}
                                            onMouseEnter={() => !hasRated && setHoveredRating(star)}
                                            onMouseLeave={() => !hasRated && setHoveredRating(0)}
                                        >
                                            ★
                                        </span>
                                    ))}
                                </div>
                                <div className="rating-info">
                                    {post.ratings?.average > 0 ? (
                                        <span>{post.ratings.average.toFixed(1)} ({post.ratings.count} {post.ratings.count === 1 ? 'rating' : 'ratings'})</span>
                                    ) : (
                                        <span>Be the first to rate</span>
                                    )}
                                    {hasRated && <span className="rated-message">Thanks for rating!</span>}
                                </div>
                            </div>
                        </header>

                        {/* Featured Image */}
                        {post.featuredImage && (
                            <div className="blog-post-image">
                                <img 
                                    src={post.featuredImage}
                                    alt={post.title}
                                />
                            </div>
                        )}

                        {/* Post Content */}
                        <div 
                            className="blog-post-content"
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />

                        {/* Tags */}
                        {post.tags && post.tags.length > 0 && (
                            <div className="blog-post-tags">
                                {post.tags.map((tag, index) => (
                                    <span key={index} className="blog-tag">{tag}</span>
                                ))}
                            </div>
                        )}

                        {/* Share Section */}
                        <div className="blog-post-share">
                            <span>Share this article:</span>
                            <div className="share-buttons">
                                <a 
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://rdsstudio.co.za/blog/${post.slug}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Share on Twitter"
                                >
                                    <i className="fab fa-x-twitter"></i>
                                </a>
                                <a 
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://rdsstudio.co.za/blog/${post.slug}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Share on Facebook"
                                >
                                    <i className="fab fa-facebook-f"></i>
                                </a>
                                <a 
                                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://rdsstudio.co.za/blog/${post.slug}`)}&title=${encodeURIComponent(post.title)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Share on LinkedIn"
                                >
                                    <i className="fab fa-linkedin-in"></i>
                                </a>
                                <a 
                                    href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - https://rdsstudio.co.za/blog/' + post.slug)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Share on WhatsApp"
                                >
                                    <i className="fab fa-whatsapp"></i>
                                </a>
                            </div>
                        </div>

                        {/* Comments Section */}
                        {post.showComments !== false && (
                            <CommentSection postId={post._id} postSlug={post.slug} />
                        )}

                        {/* Related Posts */}
                        {relatedPosts.length > 0 && (
                            <section className="related-posts">
                                <h2>Related Articles</h2>
                                <div className="related-posts-grid">
                                    {relatedPosts.map(relatedPost => (
                                        <Link 
                                            key={relatedPost._id} 
                                            to={`/blog/${relatedPost.slug}`}
                                            className="related-post-card"
                                        >
                                            {relatedPost.featuredImage && (
                                                <div className="related-post-image">
                                                    <img 
                                                        src={relatedPost.featuredImage} 
                                                        alt={relatedPost.title}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            )}
                                            <div className="related-post-content">
                                                <span className="related-post-category">{relatedPost.category}</span>
                                                <h3>{relatedPost.title}</h3>
                                                <p>{relatedPost.excerpt}</p>
                                                <span className="related-post-date">{new Date(relatedPost.publishDate || relatedPost.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Back to Blog */}
                        <div className="back-to-blog">
                            <Link to="/blog" className="btn btn-secondary">
                                <i className="fas fa-arrow-left"></i> Back to All Articles
                            </Link>
                        </div>
                    </div>
                </article>
            </main>
            <Footer />
        </div>
    )
}

export default BlogPostPage
